import { type JSX, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { ApiFehler } from '../api/fehler';
import {
  bearbeitung as ladeBearbeitung,
  kategorieSetzen,
  oeffnenBeenden,
  oeffnenMelden,
  statusSetzen,
  vorgangUebernehmen,
  zuweisen,
  type Bearbeitungsansicht,
  type Nachrichtzeile,
  type Vorgangszeile,
} from '../api/support';
import { alsTag } from '../datum';
import { AbTabletHinweis } from '../komponenten/AbTablet';
import { Hinweis, Meldung } from '../komponenten/Meldung';
import { useTelefon } from '../komponenten/telefon';
import { Antwort } from './Antwort';
import { Bearbeitungsleiste, type Leistenhandlungen } from './Bearbeitungsleiste';
import { fange, uhrzeit } from './bearbeitungshilfe';
import { Folgeaktionsliste, Folgeaktionswahl, type Folgewege, Wiedervorlagedialog } from './Folgeaktionen';
import { KiBereich } from './KiBereich';
import { naechsterSchritt, type Schritthandlung } from './naechsterSchritt';
import { Nachrichtenverlauf } from './Nachrichtenverlauf';
import { Internbereich } from './Notizen';
import { Schrittleiste } from './Schrittleiste';
import { Abtrennen, Zusammenfuehrdialog } from './Zusammenfuehren';

/**
 * DIE BEARBEITUNG EINES VORGANGS (SUP-14 bis SUP-27, SUP-48; Auftrag CRM-178).
 *
 * Oben, was man vor dem Lesen wissen muss: eine Terminabsage des Kunden
 * (SUP-48) und wer den Vorgang gerade auch offen hat (SUP-22). Darunter die
 * Leiste (Status, Zuständig, Fällig, Kategorie, Ziele), die Handlungen und
 * der Verlauf — Kundenverkehr und Internes getrennt (SUP-21).
 *
 * AUF DEM TELEFON nur lesen (ÄA-63, CRM-106): Leiste als Text, keine
 * Antwort, keine Notiz, keine Folgeaktion; die KI-Auslesung ist lesbar.
 *
 * Die KI-Auslesung (SUP-14, CRM-179) steht über dem Verlauf; ihre Schritte
 * führen in dieselben Masken wie die Folgeaktionen.
 *
 * DER AUFBAU DER AKTE (Auftrag CRM-188): Kundenverkehr und Antwort links
 * breit, rechts die Bearbeitung (Leiste, Handlungen, Kunde und Bezüge) und
 * darunter das Interne. Unter etwa 1100 px untereinander — die Bearbeitung
 * zuerst, wie zuvor im Seitenteil, dann der Verlauf, zuletzt das Interne.
 * Kein Teil klebt oder rollt in sich: Ein Aufklapper oder Dialog darin wurde
 * im klebenden Seitenteil auf dessen Kasten zugeschnitten (CRM-187).
 *
 * VORGANG MIT GETRENNTEN NACHRICHTEN UND NÄCHSTEM SCHRITT (Auftrag CRM-196).
 * Wunsch von Ulf (11.10.2026): „strukturiere die Postfachseite etwas um. wenn
 * ein Vorgang offen ist, ist die Trennung zwischen den einzelnen
 * Emailverläufen nicht klar erkennbar, ebenso der Prozess, welcher schritt
 * als nächstes folgt." Oben die Schrittleiste (naechsterSchritt.ts), im
 * Verlauf jede Nachricht als Karte (Nachrichtenverlauf.tsx). Die Bearbeitung
 * folgt den Schritten: Leiste (Eingang, Übernehmen), Kunde (Zuordnen), dann
 * die Handlungen — „Antworten" als Hauptknopf zuletzt in der Reihe (CRM-82).
 */

/** Ein Teil der Akte in Sicht bringen und den Fokus dorthin setzen (CRM-196). */
function zeigeAn(ziel: Element | null | undefined): void {
  if (!(ziel instanceof HTMLElement)) return;
  // jsdom kennt scrollIntoView nicht; dort genügt der Fokus.
  if (typeof ziel.scrollIntoView === 'function') ziel.scrollIntoView({ block: 'start', behavior: 'smooth' });
  if (!ziel.hasAttribute('tabindex')) ziel.setAttribute('tabindex', '-1');
  ziel.focus({ preventScroll: true });
}

/** SUP-22: so oft meldet die Ansicht „ich habe den Vorgang offen". */
const TAKT_MS = 60_000;

type Dialogwahl = 'folgeaktion' | 'wiedervorlage' | 'zusammenfuehren' | null;

export function Vorgangsbearbeitung({
  vorgang: v,
  nachrichten,
  darfBearbeiten,
  ich,
  folgewege = {},
  zuordnung,
  nachricht,
  beiAenderung,
  beiVorgang,
  beiKunde,
  handlungen,
}: {
  readonly vorgang: Vorgangszeile;
  readonly nachrichten: readonly Nachrichtzeile[];
  /** `support/bearbeiten`. */
  readonly darfBearbeiten: boolean;
  /** Der angemeldete Benutzer (SUP-18: „Übernehmen" fehlt bei eigenen Vorgängen). */
  readonly ich?: string | undefined;
  readonly folgewege?: Folgewege | undefined;
  /** Kunde und Bezüge (CRM-177). */
  readonly zuordnung: ReactNode;
  /** Die Darstellung einer Nachricht (Kopf, Text, Anhänge). */
  readonly nachricht: (n: Nachrichtzeile) => ReactNode;
  /** Lädt Vorgang und Liste neu. */
  readonly beiAenderung: () => Promise<void>;
  /** Einen anderen Vorgang öffnen — nach Zusammenführen oder Abtrennen. */
  readonly beiVorgang?: ((id: string) => void) | undefined;
  /** KI-03: eine Quelle „Kunde" öffnen. */
  readonly beiKunde?: ((id: string) => void) | undefined;
  /** Weitere Handlungen der Maske — „Als ungelesen markieren" im Posteingang. */
  readonly handlungen?: ReactNode;
}): JSX.Element {
  const telefon = useTelefon();
  const bearbeitbar = darfBearbeiten && !telefon;
  const [ansicht, setzeAnsicht] = useState<Bearbeitungsansicht | null>(null);
  const [ladefehler, setzeLadefehler] = useState<ApiFehler | null>(null);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [laeuft, setzeLaeuft] = useState(false);
  const [antwortOffen, setzeAntwortOffen] = useState(false);
  const [dialog, setzeDialog] = useState<Dialogwahl>(null);
  const [wiedervorlage, setzeWiedervorlage] = useState<{ readonly faelligAm: string | null; readonly beschreibung: string } | undefined>(undefined);
  const [abgetrennt, setzeAbgetrennt] = useState<{ readonly vorgang: string; readonly nummer: string } | null>(null);
  const steuerungRef = useRef<HTMLDivElement>(null);
  const verlaufRef = useRef<HTMLElement>(null);
  const nebenbeiRef = useRef<HTMLDivElement>(null);
  /** CRM-196: Nach „Antwort beginnen" rollt die Akte zur Antwort, sobald sie steht. */
  const zurAntwort = useRef(false);

  const laden = useCallback(async (hintergrund = false): Promise<Bearbeitungsansicht | null> => {
    try {
      const neu = await ladeBearbeitung(v.id, hintergrund);
      setzeAnsicht(neu);
      setzeLadefehler(null);
      return neu;
    } catch (f) {
      fange(setzeLadefehler, f);
      return null;
    }
  }, [v.id]);

  useEffect(() => {
    void laden();
  }, [laden]);

  // SUP-22: offen melden, jede Minute erneuern (und dabei neu laden), beim Verlassen abmelden.
  useEffect(() => {
    if (!bearbeitbar) return;
    const melden = (hintergrund: boolean): void => {
      oeffnenMelden(v.id, hintergrund).catch(() => {
        // Die Meldung ist ein Hinweis für die anderen; scheitert sie, arbeitet man trotzdem weiter.
      });
    };
    melden(false);
    // BEN-07: Der Takt hält die Sitzung nicht wach — wer den Vorgang nur offen lässt, wird abgemeldet.
    const takt = window.setInterval(() => {
      melden(true);
      void laden(true);
    }, TAKT_MS);
    return () => {
      window.clearInterval(takt);
      oeffnenBeenden(v.id).catch(() => {
        // Ohne Abmeldung läuft die Anzeige beim anderen nach dem Takt des Servers aus.
      });
    };
  }, [v.id, bearbeitbar, laden]);

  const alles = async (): Promise<Bearbeitungsansicht | null> => {
    const [, neu] = await Promise.all([beiAenderung(), laden()]);
    return neu;
  };

  const tue = async (was: () => Promise<unknown>): Promise<void> => {
    setzeLaeuft(true);
    setzeFehler(null);
    try {
      await was();
      await alles();
    } catch (f) {
      fange(setzeFehler, f);
    } finally {
      setzeLaeuft(false);
    }
  };

  const leiste: Leistenhandlungen = {
    status: (s) => void tue(() => statusSetzen(v.id, s)),
    zuweisen: (an) => void tue(() => zuweisen(v.id, an)),
    faellig: (am) => void tue(() => zuweisen(v.id, { faelligAm: am })),
    kategorie: (k) => void tue(() => kategorieSetzen(v.id, k)),
    uebernehmen: () => void tue(() => vorgangUebernehmen(v.id)),
  };

  // SUP-48: aus der Bearbeitung, solange sie fehlt aus der Zeile der Liste.
  const absage = ansicht !== null ? ansicht.absage : v.bearbeitung.absage;
  const aufgegangen = ansicht?.zusammengefuehrtIn ?? null;
  const handeln = bearbeitbar && ansicht !== null && aufgegangen === null;

  // CRM-196: der nächste Schritt aus Status, Zuordnung, Verlauf, Versand und Folgeaktionen.
  const schritt = ansicht !== null && aufgegangen === null
    ? naechsterSchritt({
      status: ansicht.status,
      kunde: v.kunde,
      zuordnung: v.zuordnung,
      sicherheit: v.sicherheit,
      nachrichten,
      versand: ansicht.versand,
      folgeaktionen: ansicht.folgeaktionen,
    })
    : null;

  useEffect(() => {
    if (!antwortOffen || !zurAntwort.current) return;
    zurAntwort.current = false;
    zeigeAn(verlaufRef.current?.querySelector('[aria-label="Antwort"]'));
  }, [antwortOffen]);

  const schrittTun = (h: Schritthandlung): void => {
    switch (h.art) {
      case 'uebernehmen':
        leiste.uebernehmen();
        return;
      case 'zuordnung':
        zeigeAn(steuerungRef.current?.querySelector('.zuordnung'));
        return;
      case 'antworten':
        if (antwortOffen) zeigeAn(verlaufRef.current?.querySelector('[aria-label="Antwort"]'));
        else {
          zurAntwort.current = true;
          setzeAntwortOffen(true);
        }
        return;
      case 'folgeaktionen':
        zeigeAn(nebenbeiRef.current?.querySelector('[aria-label="Folgeaktionen des Vorgangs"]'));
        return;
      case 'status':
        leiste.status(h.status);
        return;
    }
  };

  return (
    <>
      {/* SUP-48: vor allem anderen, auch auf dem Telefon. */}
      {absage !== null && (
        <p className="warnung vorgang__absage" role="status">
          Betroffen von Terminabsage: {absage.produkt} am {alsTag(absage.datum) ?? absage.datum}
        </p>
      )}
      {/* CRM-196: unter dem Titel, was als Nächstes folgt. */}
      {schritt !== null && <Schrittleiste schritt={schritt} handeln={handeln} laeuft={laeuft} beiHandlung={schrittTun} />}
      {aufgegangen !== null && (
        <p className="hinweiszeile" role="status">
          Dieser Vorgang ist in {aufgegangen.nummer} aufgegangen.{' '}
          {beiVorgang !== undefined && (
            <button type="button" className="knopf knopf--klein" data-auch-telefon onClick={() => beiVorgang(aufgegangen.id)}>
              {aufgegangen.nummer} öffnen
            </button>
          )}
        </p>
      )}
      {/* SUP-22 */}
      {ansicht !== null && ansicht.bearbeitende.length > 0 && (
        <p className="warnung" role="status">
          Wird gerade auch von{' '}
          {ansicht.bearbeitende.map((b) => `${b.name} (seit ${uhrzeit(b.seit)})`).join(', ')} bearbeitet.
        </p>
      )}
      <AbTabletHinweis an={darfBearbeiten} />
      {ladefehler !== null && <Meldung fehler={ladefehler} />}
      {fehler !== null && <Meldung fehler={fehler} />}
      {abgetrennt !== null && (
        <Hinweis ueberschrift={`Als eigener Vorgang ${abgetrennt.nummer} abgetrennt.`}>
          {beiVorgang !== undefined && (
            <div className="knopfreihe">
              <button type="button" className="knopf knopf--klein" onClick={() => beiVorgang(abgetrennt.vorgang)}>
                {abgetrennt.nummer} öffnen
              </button>
            </div>
          )}
        </Hinweis>
      )}

      <div className="akte vorgang__rumpf">
        {/* CRM-196: in der Reihenfolge der Schritte — Eingang (Leiste), Zuordnen, dann Antworten und Folgeaktion. */}
        <div ref={steuerungRef} className="akte__seite vorgang__steuerung">
          {ansicht !== null && (
            <Bearbeitungsleiste ansicht={ansicht} bearbeitbar={bearbeitbar} ich={ich} laeuft={laeuft} tue={leiste} />
          )}

          {zuordnung}

          {handeln && (
            <div className="knopfreihe vorgang__handlungen">
              <button type="button" className="knopf knopf--klein" disabled={laeuft} onClick={() => setzeDialog('zusammenfuehren')}>
                Zusammenführen …
              </button>
              <button type="button" className="knopf knopf--klein" disabled={laeuft} onClick={() => setzeDialog('folgeaktion')}>
                Folgeaktion …
              </button>
              {/* Der Hauptknopf steht zuletzt in der Reihe (CRM-82). */}
              {!antwortOffen && (
                <button type="button" className="knopf knopf--haupt knopf--klein" disabled={laeuft} onClick={() => setzeAntwortOffen(true)}>
                  Antworten
                </button>
              )}
            </div>
          )}
          {handlungen}
        </div>

        <section ref={verlaufRef} className="akte__haupt vorgang__verlauf" aria-label="Kundenverkehr">
          {ansicht !== null && aufgegangen === null && (
            <KiBereich
              vorgang={v}
              ich={ich}
              bearbeitbar={handeln}
              wege={{ ...folgewege, kunde: beiKunde }}
              beiKategorie={leiste.kategorie}
              beiAntworten={() => setzeAntwortOffen(true)}
              beiWiedervorlage={(vb) => {
                setzeWiedervorlage(vb);
                setzeDialog('wiedervorlage');
              }}
            />
          )}

          <h3>Kundenverkehr</h3>
          {/* SUP-04, SUP-19: je Nachricht eine Karte mit Richtung, Absender, Zeitpunkt und Versandstand (CRM-196). */}
          <Nachrichtenverlauf
            nachrichten={nachrichten}
            versand={ansicht?.versand}
            nachricht={nachricht}
            fuss={(n) =>
              // SUP-15: nur eingehende Nachrichten, und nur, wenn eine andere bleibt.
              handeln && n.richtung === 'eingehend' && nachrichten.length > 1 ? (
                <Abtrennen
                  nachricht={n}
                  beiAbgetrennt={async (neu) => {
                    setzeAbgetrennt(neu);
                    await alles();
                  }}
                />
              ) : null}
          />
          {handeln && antwortOffen && (
            <Antwort
              vorgang={v}
              ansicht={ansicht}
              beiGesendet={async () => {
                setzeAntwortOffen(false);
                await alles();
              }}
              beiKonflikt={async () => (await alles())?.stand ?? null}
              beiSchliessen={() => setzeAntwortOffen(false)}
              quellenwege={{ ...folgewege, kunde: beiKunde }}
            />
          )}
        </section>

        {ansicht !== null && (
          <div ref={nebenbeiRef} className="akte__seite vorgang__nebenbei">
            <Internbereich
              vorgang={v.id}
              notizen={ansicht.notizen}
              ereignisse={ansicht.ereignisse}
              bearbeitbar={handeln}
              beiNotiz={async () => {
                await laden();
              }}
              folgeaktionen={
                <Folgeaktionsliste
                  vorgang={v.id}
                  folgeaktionen={ansicht.folgeaktionen}
                  wege={folgewege}
                  bearbeitbar={handeln}
                  beiAenderung={async () => {
                    await laden();
                  }}
                />
              }
            />
          </div>
        )}
      </div>

      {dialog === 'folgeaktion' && (
        <Folgeaktionswahl
          vorgang={v}
          wege={folgewege}
          beiWiedervorlage={() => {
            setzeWiedervorlage(undefined);
            setzeDialog('wiedervorlage');
          }}
          beiSchliessen={() => setzeDialog(null)}
        />
      )}
      {dialog === 'wiedervorlage' && (
        <Wiedervorlagedialog
          vorgang={v}
          vorbelegung={wiedervorlage}
          beiAngelegt={async () => {
            setzeDialog(null);
            await laden();
          }}
          beiSchliessen={() => setzeDialog(null)}
        />
      )}
      {dialog === 'zusammenfuehren' && (
        <Zusammenfuehrdialog
          vorgang={v}
          beiZusammengefuehrt={(ziel) => {
            setzeDialog(null);
            if (beiVorgang !== undefined) beiVorgang(ziel.vorgang);
            else void alles();
          }}
          beiSchliessen={() => setzeDialog(null)}
        />
      )}
    </>
  );
}
